// Ampliación de baterías (Diego, 27-oct-2026): la captura de la pantalla «Ampliación» como texto (como lo teclea el vendedor) y
// su conversión a ediciones del borrador (/piezas/…, cada una con su «antes»). El precio NO se teclea: el vendedor pone COSTOS y
// el servidor calcula `calculo_piezas` con la regla de Diego. Aquí nunca se calcula un precio.
// Ojo (lección pagada): Number("") da 0. Un campo vacío es null, nunca 0.
import { ANTICIPO_AMPLIACION_PCT, ENTREGA_AMPLIACION_DIAS, MSI_TOPE_LISTA_CON_IVA, PiezasAmpliacion, iguales, kwhComercial, nombreBateria, numero, pesos, precargaDeFicha, reservaDeFicha } from '@cotizador/compartido';
import type { Aviso, BateriaAmpliacion, CalculoAmpliacion, EdicionCampo, Hoja2Ampliacion, VistaPrecios } from '@cotizador/compartido';
import { numeroDe } from './cuestionario.ts';
import { reemplazo } from './ediciones.ts';

/** Una batería que se puede sumar (de la tabla de precios o del catálogo del motor), sin costos. */
export type BateriaVista = NonNullable<VistaPrecios['baterias_ampliacion']>[number];

export const MOTIVO_AMPLIACION = 'Ampliación de baterías';

/** Llave estable de una batería para el selector: el renglón de la tabla o, si viene del catálogo, marca y modelo. */
export function claveBateria(b: Pick<BateriaAmpliacion, 'id_tabla' | 'marca' | 'modelo'>): string {
  return b.id_tabla ? `tabla:${b.id_tabla}` : `catalogo:${b.marca.trim()}|${b.modelo.trim()}`;
}

/** Lo que se guarda en `piezas.bateria` (la vista sin «costo_en_tabla»). */
export function bateriaDeVista(v: BateriaVista): BateriaAmpliacion {
  const { costo_en_tabla: _c, ...b } = v;
  return { ...b };
}

/** «32.4» · «43.2» · «16»: los kWh como los dice el nombre comercial (un decimal, sin ceros de más). */
export function kwhCorto(n: number): string {
  const r = Math.round(n * 10) / 10;
  return numero(r, Number.isInteger(r) ? 0 : 1);
}

/** «Fortress eFlex MAX 5.4 · 5.4 kWh · hasta 16 en paralelo». */
export function textoBateria(b: Pick<BateriaAmpliacion, 'marca' | 'modelo' | 'kwh_nominales_por_modulo' | 'modulos_max'>): string {
  return `${b.marca} ${b.modelo} · ${kwhCorto(kwhComercial(b.kwh_nominales_por_modulo))} kWh · hasta ${b.modulos_max} en paralelo`;
}

/** Lo que dice la ficha con las cantidades: de cuántos a cuántos kWh y si pasa del máximo en paralelo. null = falta algo. */
export function rangoAmpliacion(b: Pick<BateriaAmpliacion, 'kwh_nominales_por_modulo' | 'modulos_max'> | null, actuales: number | null, nuevas: number | null): { hoy: number; con: number; total: number; maximo: number; pasa: boolean } | null {
  if (!b || actuales === null || nuevas === null || !Number.isInteger(actuales) || !Number.isInteger(nuevas) || actuales < 1 || nuevas < 1) return null;
  const kwh = kwhComercial(b.kwh_nominales_por_modulo);
  const total = actuales + nuevas;
  return { hoy: actuales * kwh, con: total * kwh, total, maximo: b.modulos_max, pasa: total > b.modulos_max };
}

/** Los 3 MSI como los imprime la plantilla: `msi_3` del cálculo (lista ÷ 3), o null arriba del tope de la adenda A0b
 *  ($500,000 de lista): ahí el PDF no los imprime y la pantalla tampoco los promete (verificación del 27-oct). Nunca lista ÷ 3. */
export function textoMsi(c: Pick<CalculoAmpliacion, 'msi_3' | 'lista_con_iva'>, conLista = false): string {
  const msi = c.msi_3 ?? null;
  if (msi === null) return `Sin meses sin intereses: la lista pasa de ${pesos(MSI_TOPE_LISTA_CON_IVA, { enteros: true })}`;
  return `3 MSI: 3 × ${pesos(msi)}${conLista ? ` (${pesos(c.lista_con_iva)} con IVA)` : ''}`;
}

/** Lo que la plantilla de PDF conectada sabe hacer con una ampliación, MEDIDO por el servidor (DisponibilidadPdf.capacidades) y
 *  dicho en sus avisos de origen «pdf» (servidor → avisosPdf): sin `sin_desglose` el PDF no sale (aviso PDF_FORMATO_NO_EXISTE en
 *  /piezas); sin `ampliacion_baterias` sale sin la hoja 2 (aviso VARIANTE_NO_SOPORTADA en /piezas/hoja2). La configuración pública
 *  (ConfigPublica.pdf) no trae las capacidades: estos avisos son la medida que llega a la pantalla. */
export function plantillaPiezas(avisos: readonly Pick<Aviso, 'origen' | 'codigo' | 'campo' | 'resuelto' | 'mensaje'>[]): { sin_desglose: boolean; hoja2: boolean; mensaje: string | null } {
  const de = (codigo: Aviso['codigo'], campo: string) => avisos.find((a) => a.origen === 'pdf' && a.codigo === codigo && a.campo === campo && !a.resuelto) ?? null;
  const sinDesglose = de('PDF_FORMATO_NO_EXISTE', '/piezas');
  // `mensaje`: el del aviso tal cual (MENSAJE_SIN_DESGLOSE de paquetes/pdf: «… Ya se le avisó a Diego …»), no uno escrito aquí
  return { sin_desglose: !sinDesglose, hoja2: !de('VARIANTE_NO_SOPORTADA', '/piezas/hoja2'), mensaje: sinDesglose?.mensaje ?? null };
}

/** «÷ 0.62: 30 % + comisión 6 % + imprevistos 2 %» (la regla con que el servidor sacó el precio). */
export function textoRegla(r: NonNullable<CalculoAmpliacion['regla']>): string {
  return `÷ ${numero(r.divisor, 2)}: ${numero(r.margen_pct, r.margen_pct % 1 ? 1 : 0)} % + comisión ${numero(r.comision_pct, r.comision_pct % 1 ? 1 : 0)} % + imprevistos ${numero(r.imprevistos_pct, r.imprevistos_pct % 1 ? 1 : 0)} %`;
}

/** «452» → «04:52», «1652» → «16:52», «4:52» → «04:52», «16.52» → «16:52». Lo que no se entiende se queda igual (el esquema lo
 *  rechaza con su mensaje). En el teclado numérico del celular no hay «:». */
export function horaDe(s: string): string {
  const t = s.trim();
  const m = /^(\d{1,2})[:.,h ]?(\d{2})$/.exec(t);
  if (!m) return t;
  return `${m[1]!.padStart(2, '0')}:${m[2]}`;
}

export interface FormaAmpliacion {
  bateria: string;
  actuales: string;
  nuevas: string;
  costoOrigen: 'tabla' | 'proveedor';
  costoProveedor: string;
  fuenteProveedor: string;
  material: string;
  instalacion: string;
  viaticos: string;
  sujeto: boolean;
  anticipo: string;
  entregaMin: string;
  entregaMax: string;
  conHoja2: boolean;
  corte: string;
  reserva: string;
  nocheFecha: string;
  nocheComo: string;
  nocheInicio: string;
  nocheInicioPct: string;
  nocheCorte: string;
  nocheSol: string;
  normalMax: string;
  conRecarga: boolean;
  recargaLlenas: string;
  recargaFecha: string;
  recargaKw: string;
  conPaneles: boolean;
  panelesCantidad: string;
  panelesW: string;
  fuente: string;
}

const s = (n: number | null | undefined) => (n === null || n === undefined ? '' : String(n));

/** El costo de proveedor guardado es de OTRA batería que `b` (su `para` no es ella): no vale para esta. */
export function costoDeOtraBateria(p: Pick<PiezasAmpliacion, 'costo_bateria'> | null | undefined, b: Pick<BateriaAmpliacion, 'marca' | 'modelo'> | null): string | null {
  const c = p?.costo_bateria;
  return c?.origen === 'proveedor' && b && c.para !== nombreBateria(b) ? c.para : null;
}

/** La hoja 2 en blanco con lo que se precarga de la ficha (reserva del fabricante y de dónde salen las horas). */
function hoja2EnBlanco(b: BateriaAmpliacion | null): Pick<FormaAmpliacion, 'corte' | 'reserva' | 'fuente'> {
  const f = b ? precargaDeFicha(b) : null;
  return { corte: '', reserva: s(f?.reserva_fabricante_pct ?? null), fuente: f?.fuente ?? '' };
}

/** La forma con lo guardado. Sin hoja 2, sus campos quedan con lo de la ficha (para cuando se prenda). */
export function formaAmpliacion(p: PiezasAmpliacion | null | undefined, enTabla: boolean | null): FormaAmpliacion {
  const x = p ?? null;
  const h = x?.hoja2 ?? null;
  const b = x?.bateria ?? null;
  const blanco = hoja2EnBlanco(b);
  const c = x?.costo_bateria ?? null;
  return {
    bateria: b ? claveBateria(b) : '',
    actuales: s(x?.actuales),
    nuevas: s(x?.nuevas),
    // sin costo capturado: de la tabla si la batería lo trae; si no, del proveedor. Un costo de proveedor de OTRA batería (el
    // servidor lo marca en rojo) llega vacío: se teclea el de esta, nunca se reenvía con otro nombre.
    costoOrigen: c ? c.origen : enTabla === false ? 'proveedor' : 'tabla',
    costoProveedor: c?.origen === 'proveedor' && !costoDeOtraBateria(x, b) ? String(c.sin_iva) : '',
    fuenteProveedor: c?.origen === 'proveedor' && !costoDeOtraBateria(x, b) ? c.fuente : '',
    material: s(x?.costo_material_sin_iva),
    instalacion: s(x?.costo_instalacion_sin_iva),
    viaticos: s(x?.costo_viaticos_sin_iva),
    sujeto: x?.precio_sujeto_a_confirmacion ?? false,
    anticipo: s(x?.anticipo_pct ?? ANTICIPO_AMPLIACION_PCT),
    entregaMin: s(x?.entrega_dias_habiles.min ?? ENTREGA_AMPLIACION_DIAS.min),
    entregaMax: s(x?.entrega_dias_habiles.max ?? ENTREGA_AMPLIACION_DIAS.max),
    conHoja2: h !== null,
    corte: h ? String(h.corte_pct) : blanco.corte,
    reserva: h ? s(h.reserva_fabricante_pct) : blanco.reserva,
    nocheFecha: h?.noche.fecha ?? '',
    nocheComo: h?.noche.como ?? '',
    nocheInicio: h?.noche.inicio ?? '',
    nocheInicioPct: s(h?.noche.inicio_pct),
    nocheCorte: h?.noche.corte ?? '',
    nocheSol: h?.noche.sol ?? '',
    normalMax: s(h?.noche_normal_max_pct),
    conRecarga: h?.recarga != null,
    recargaLlenas: h?.recarga?.llenas_a_las ?? '',
    recargaFecha: h?.recarga?.fecha ?? '',
    recargaKw: s(h?.recarga?.carga_kw),
    conPaneles: h?.paneles != null,
    panelesCantidad: s(h?.paneles?.cantidad),
    panelesW: s(h?.paneles?.w),
    fuente: h ? h.fuente : blanco.fuente,
  };
}

/** Al cambiar de batería (verificación de ventas del 27-oct: una Pylontech salía con el costo de la Fortress, «Rentable»):
 *  - el costo de la anterior NUNCA se queda: «de la tabla» si la nueva lo trae; si no, vacío para teclear el del proveedor
 *    (si se vuelve a la batería guardada, regresa su costo guardado: `guardada`, la forma de lo guardado);
 *  - la reserva del fabricante es la de la ficha de la nueva (no se teclea);
 *  - la fuente de las horas, si era la de la ficha anterior (no la tecleó nadie), la de la nueva. */
export function conOtraBateria(f: FormaAmpliacion, antes: BateriaAmpliacion | null, nueva: BateriaVista, guardada: FormaAmpliacion | null = null): FormaAmpliacion {
  const clave = claveBateria(nueva);
  if (clave === f.bateria) return f;
  const viejo = hoja2EnBlanco(antes);
  const nuevo = hoja2EnBlanco(bateriaDeVista(nueva));
  const costo: Pick<FormaAmpliacion, 'costoOrigen' | 'costoProveedor' | 'fuenteProveedor'> =
    guardada && guardada.bateria === clave
      ? { costoOrigen: guardada.costoOrigen, costoProveedor: guardada.costoProveedor, fuenteProveedor: guardada.fuenteProveedor }
      : { costoOrigen: nueva.costo_en_tabla ? 'tabla' : 'proveedor', costoProveedor: '', fuenteProveedor: '' };
  return {
    ...f,
    bateria: clave,
    ...costo,
    reserva: nuevo.reserva,
    fuente: f.fuente === viejo.fuente || !f.fuente.trim() ? nuevo.fuente : f.fuente,
  };
}

/** Lo que dice la ficha de la reserva del fabricante (no se teclea: RESERVAS_FABRICANTE, medida en su manual). */
export function textoReserva(b: Pick<BateriaAmpliacion, 'marca' | 'modelo'> | null): string {
  if (!b) return 'Elige la batería: la reserva sale de su ficha.';
  const r = reservaDeFicha(b);
  return r === null ? 'Esta batería no trae reserva medida: la hoja no la menciona' : `${r} % · del manual del fabricante`;
}

/** El día siguiente a una fecha AAAA-MM-DD (la recarga que vale es la de la mañana SIGUIENTE a la noche medida), o null. */
export function diaSiguiente(fecha: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha)) return null;
  const t = Date.parse(`${fecha}T12:00:00Z`);
  if (Number.isNaN(t) || new Date(t).toISOString().slice(0, 10) !== fecha) return null;
  return new Date(t + 864e5).toISOString().slice(0, 10);
}

export type ErroresAmpliacion = Partial<Record<string, string>>;

const T = (x: string) => x.trim();

/** Entero en [min, max] o null si está vacío; NaN si no sirve (con su error). */
function entero(v: string, campo: string, min: number, max: number, mensaje: string, e: ErroresAmpliacion, obligatorio = false): number | null {
  const n = numeroDe(v);
  if (n === null) {
    if (obligatorio) e[campo] = mensaje;
    return null;
  }
  if (!Number.isInteger(n) || n < min || n > max) {
    e[campo] = mensaje;
    return Number.NaN;
  }
  return n;
}

function pesosDe(v: string, campo: string, e: ErroresAmpliacion): number | null {
  const n = numeroDe(v);
  if (n === null) return null;
  if (!Number.isFinite(n) || n < 0 || n > 2_000_000) {
    e[campo] = 'Escribe un costo en pesos, sin IVA (0 o más).';
    return Number.NaN;
  }
  return Math.round(n * 100) / 100;
}

const FECHA = /^\d{4}-\d{2}-\d{2}$/;

/** Lo que la forma deja en `piezas` (o los errores por campo, con la llave del campo: «/piezas/hoja2/noche/corte»). `guardadas`:
 *  lo que hay hoy (la batería que ya no esté en la lista se respeta; un costo capturado no se borra por dejar vacío el del
 *  proveedor). */
export function piezasDeForma(
  f: FormaAmpliacion,
  baterias: readonly BateriaVista[],
  guardadas: PiezasAmpliacion | null | undefined,
  /** Ampliación AJENA para quien no ve costos (vista.ts: le llegan en null y el servidor da 403 si los edita): los costos se
   *  quedan como llegaron, nunca se mandan ni se revisan aquí (los captura el vendedor de esa cotización o Diego). */
  op: {
    costosAjenos?: boolean;
    /** Quien no ve costos (Diego, 27-oct noche): anticipo y entrega son FIJOS (80 %, de 7 a 15 días hábiles); se quedan como
     *  están guardados y nunca van en las ediciones (el servidor da 403 si los cambia). */
    condicionesFijas?: boolean;
  } = {},
): { piezas: PiezasAmpliacion | null; errores: ErroresAmpliacion } {
  const e: ErroresAmpliacion = {};
  const actual = guardadas?.bateria ?? null;
  const vista = baterias.find((b) => claveBateria(b) === f.bateria) ?? null;
  const bateria = vista ? bateriaDeVista(vista) : actual && claveBateria(actual) === f.bateria ? actual : null;
  if (f.bateria && !bateria) e['/piezas/bateria'] = 'Esa batería ya no está en la lista: elige otra.';
  // ¿Es otra batería que la guardada? (su costo de proveedor no sirve para esta)
  const otraBateria = !!actual && !!bateria && claveBateria(actual) !== claveBateria(bateria);
  const actuales = entero(f.actuales, '/piezas/actuales', 1, 64, 'Baterías que ya tiene: un número entero de 1 a 64.', e);
  const nuevas = entero(f.nuevas, '/piezas/nuevas', 1, 64, 'Baterías que se suman: un número entero de 1 a 64.', e);

  let costo: PiezasAmpliacion['costo_bateria'] = null;
  if (op.costosAjenos) costo = guardadas?.costo_bateria ?? null;
  else if (f.costoOrigen === 'tabla') {
    if (vista && !vista.costo_en_tabla) e['/piezas/costo_bateria'] = 'La tabla de precios no trae el costo de esta batería: tecléalo del proveedor.';
    else costo = { origen: 'tabla' };
  } else {
    const monto = pesosDe(f.costoProveedor, '/piezas/costo_bateria/sin_iva', e);
    const fuente = T(f.fuenteProveedor);
    if (monto !== null && !Number.isNaN(monto) && monto <= 0) e['/piezas/costo_bateria/sin_iva'] = 'El costo de cada batería va arriba de $0.';
    if (monto !== null && !fuente) e['/piezas/costo_bateria/fuente'] = 'Escribe de dónde sale: proveedor y su cotización (Diego la ve en la bitácora).';
    else if (monto === null && fuente) e['/piezas/costo_bateria/sin_iva'] = 'Escribe el costo de cada batería, sin IVA.';
    else if (fuente && fuente.length < 2) e['/piezas/costo_bateria/fuente'] = 'Escribe de dónde sale (2 letras o más).';
    // Pasar a «Del proveedor» y dejarlo vacío borraría el costo que ya tenía (y el precio): se pide, no se borra en silencio.
    else if (monto === null && !fuente && guardadas?.costo_bateria)
      e['/piezas/costo_bateria/sin_iva'] =
        (otraBateria || costoDeOtraBateria(guardadas, bateria)) && bateria
          ? `Teclea el costo de la ${nombreBateria(bateria)} con la cotización del proveedor: el de la batería anterior no sirve para esta.`
          : 'Escribe el costo de cada batería del proveedor (o vuelve a «De la tabla de precios»).';
    if (monto !== null && !Number.isNaN(monto) && monto > 0 && fuente.length >= 2) {
      // El costo de proveedor va AMARRADO a su batería (`para`, compartido 5b8e261). Sin batería no hay de qué es. Un costo
      // guardado de OTRA batería no llega a la forma (formaAmpliacion lo deja vacío): lo que se manda aquí lo tecleó la persona
      // para ESTA batería, nunca se le cambia de batería en silencio.
      if (!bateria) e['/piezas/bateria'] = 'Elige la batería: el costo del proveedor es de una batería.';
      else costo = { origen: 'proveedor', sin_iva: monto, fuente: fuente.slice(0, 200), para: nombreBateria(bateria).slice(0, 80) };
    }
  }
  const material = op.costosAjenos ? (guardadas?.costo_material_sin_iva ?? null) : pesosDe(f.material, '/piezas/costo_material_sin_iva', e);
  const instalacion = op.costosAjenos ? (guardadas?.costo_instalacion_sin_iva ?? null) : pesosDe(f.instalacion, '/piezas/costo_instalacion_sin_iva', e);
  const viaticos = op.costosAjenos ? (guardadas?.costo_viaticos_sin_iva ?? null) : pesosDe(f.viaticos, '/piezas/costo_viaticos_sin_iva', e);
  const fijas = op.condicionesFijas ? { anticipo: guardadas?.anticipo_pct ?? ANTICIPO_AMPLIACION_PCT, entrega: guardadas?.entrega_dias_habiles ?? ENTREGA_AMPLIACION_DIAS } : null;
  const anticipo = fijas ? fijas.anticipo : entero(f.anticipo, '/piezas/anticipo_pct', 0, 100, 'El anticipo va de 0 a 100 %.', e, true);
  const min = fijas ? fijas.entrega.min : entero(f.entregaMin, '/piezas/entrega_dias_habiles/min', 1, 90, 'Días hábiles: de 1 a 90.', e, true);
  const max = fijas ? fijas.entrega.max : entero(f.entregaMax, '/piezas/entrega_dias_habiles/max', 1, 90, 'Días hábiles: de 1 a 90.', e, true);
  if (min !== null && max !== null && !Number.isNaN(min) && !Number.isNaN(max) && min > max) e['/piezas/entrega_dias_habiles/max'] = 'El máximo no puede ser menor que el mínimo.';

  let hoja2: Hoja2Ampliacion | null = null;
  if (f.conHoja2) {
    const falta = 'Falta este dato de la noche medida.';
    const pct = (v: string, campo: string, lo: number, hi: number, msg: string) => entero(v, campo, lo, hi, msg, e, true);
    const corte = pct(f.corte, '/piezas/hoja2/corte_pct', 5, 60, 'El corte del inversor va de 5 a 60 %.');
    // La reserva del fabricante NO se teclea: la de la ficha de la batería (su manual) o null (verificación del 27-oct).
    const reserva = bateria ? reservaDeFicha(bateria) : null;
    const inicioPct = pct(f.nocheInicioPct, '/piezas/hoja2/noche/inicio_pct', 1, 100, 'La carga al empezar la noche va de 1 a 100 %.');
    const normal = pct(f.normalMax, '/piezas/hoja2/noche_normal_max_pct', 1, 100, 'Escribe el % que usa en una noche normal (1 a 100).');
    const req = (v: string, campo: string) => {
      if (!T(v)) e[campo] = falta;
      return T(v);
    };
    const fecha = req(f.nocheFecha, '/piezas/hoja2/noche/fecha');
    const como = req(f.nocheComo, '/piezas/hoja2/noche/como');
    const inicio = horaDe(req(f.nocheInicio, '/piezas/hoja2/noche/inicio'));
    const corteH = horaDe(req(f.nocheCorte, '/piezas/hoja2/noche/corte'));
    const sol = horaDe(req(f.nocheSol, '/piezas/hoja2/noche/sol'));
    const fuente = T(f.fuente);
    if (!fuente) e['/piezas/hoja2/fuente'] = 'Di de dónde salen las horas (por ejemplo «el monitoreo de tu sistema Fortress»).';
    let recarga: Hoja2Ampliacion['recarga'] = null;
    if (f.conRecarga) {
      const llenas = horaDe(req(f.recargaLlenas, '/piezas/hoja2/recarga/llenas_a_las'));
      const rf = req(f.recargaFecha, '/piezas/hoja2/recarga/fecha');
      const kw = numeroDe(f.recargaKw);
      if (kw === null || !Number.isFinite(kw) || kw <= 0 || kw > 30) e['/piezas/hoja2/recarga/carga_kw'] = 'Los kW con que cargaban: un número mayor que 0 (hasta 30).';
      recarga = { llenas_a_las: llenas, fecha: rf, carga_kw: kw ?? Number.NaN };
    }
    let paneles: Hoja2Ampliacion['paneles'] = null;
    if (f.conPaneles) {
      const cantidad = entero(f.panelesCantidad, '/piezas/hoja2/paneles/cantidad', 1, 400, 'Paneles que ya tiene: un entero de 1 a 400.', e, true);
      const w = entero(f.panelesW, '/piezas/hoja2/paneles/w', 100, 800, 'Watts de cada panel: de 100 a 800.', e, true);
      paneles = { cantidad: cantidad ?? Number.NaN, w: w ?? Number.NaN };
    }
    if (fecha && !FECHA.test(fecha)) e['/piezas/hoja2/noche/fecha'] = 'La fecha va como AAAA-MM-DD.';
    hoja2 = {
      corte_pct: corte ?? Number.NaN,
      reserva_fabricante_pct: reserva,
      noche: { fecha, como, inicio, inicio_pct: inicioPct ?? Number.NaN, corte: corteH, sol },
      noche_normal_max_pct: normal ?? Number.NaN,
      recarga,
      paneles,
      fuente,
    };
  }

  const piezas = {
    bateria,
    actuales,
    nuevas,
    costo_bateria: costo,
    costo_material_sin_iva: material,
    costo_instalacion_sin_iva: instalacion,
    costo_viaticos_sin_iva: viaticos,
    precio_sujeto_a_confirmacion: f.sujeto,
    anticipo_pct: anticipo ?? Number.NaN,
    entrega_dias_habiles: { min: min ?? Number.NaN, max: max ?? Number.NaN },
    hoja2,
  };
  // La validación final es la MISMA del servidor (PiezasAmpliacion): lo que se escape de arriba (la hora que no es HH:MM, «como»
  // que no va a media frase…) se marca en su campo con el mensaje del esquema.
  const r = PiezasAmpliacion.safeParse(piezas);
  if (!r.success)
    for (const i of r.error.issues) {
      const ruta = `/piezas/${i.path.map(String).join('/')}`;
      const campo = ruta === '/piezas/entrega_dias_habiles' ? '/piezas/entrega_dias_habiles/max' : ruta;
      if (!Object.keys(e).some((k) => k === campo || campo.startsWith(`${k}/`))) e[campo] = i.message;
    }
  return Object.keys(e).length || !r.success ? { piezas: null, errores: e } : { piezas: r.data, errores: {} };
}

/** Diferencia campo por campo entre lo guardado y lo nuevo. La batería, el costo de la batería (su forma cambia con el origen),
 *  la entrega, la recarga y los paneles van completos; la hoja 2 y su noche, dato por dato (/piezas/hoja2/noche/corte). Sin
 *  `piezas` guardadas (una ampliación creada sin captura) va todo de una vez. */
export function edicionesPiezas(antes: PiezasAmpliacion | null | undefined, despues: PiezasAmpliacion): EdicionCampo[] {
  if (!antes) return [reemplazo('/piezas', antes ?? null, despues, MOTIVO_AMPLIACION)];
  const out: EdicionCampo[] = [];
  const dif = (ruta: string, a: unknown, b: unknown) => {
    if (!iguales(a ?? null, b ?? null)) out.push(reemplazo(ruta, a ?? null, b ?? null, MOTIVO_AMPLIACION));
  };
  for (const k of Object.keys(despues) as (keyof PiezasAmpliacion)[]) {
    if (k === 'hoja2' && antes.hoja2 && despues.hoja2) {
      const ha = antes.hoja2;
      const hb = despues.hoja2;
      for (const kh of Object.keys(hb) as (keyof Hoja2Ampliacion)[]) {
        if (kh === 'noche') for (const kn of Object.keys(hb.noche) as (keyof Hoja2Ampliacion['noche'])[]) dif(`/piezas/hoja2/noche/${kn}`, ha.noche[kn], hb.noche[kn]);
        else dif(`/piezas/hoja2/${kh}`, ha[kh], hb[kh]);
      }
      continue;
    }
    dif(`/piezas/${k}`, antes[k], despues[k]);
  }
  return out;
}

/** El primer dato que falta para el precio (a donde lleva «Ver el dato» del rojo «falta…»). */
export function primerFaltante(p: PiezasAmpliacion | null | undefined): string {
  if (!p?.bateria) return '/piezas/bateria';
  if (p.actuales === null) return '/piezas/actuales';
  if (p.nuevas === null) return '/piezas/nuevas';
  if (!p.costo_bateria) return '/piezas/costo_bateria';
  if (p.costo_material_sin_iva === null) return '/piezas/costo_material_sin_iva';
  if (p.costo_instalacion_sin_iva === null) return '/piezas/costo_instalacion_sin_iva';
  return '/piezas/bateria';
}
